import React, { useState } from "react";
import { toast } from "sonner";
import { Pin, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * One unified Notes area. Everything in the CRM is already private to its owner, so there
 * is no separate "private notes" section — a note can simply be marked "Remember this"
 * (stored as note kind `remember_this`) and is pinned to the top.
 */
export default function ContactNotes({ contactId, notes = [], onChanged }) {
  const [body, setBody] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/crm/contacts/${contactId}/notes`,
        { body, kind: remember ? "remember_this" : "general" });
      setBody(""); setRemember(false);
      onChanged?.();
    } catch { toast.error("Couldn't save that note"); } finally { setBusy(false); }
  };

  const togglePin = async (note) => {
    try {
      await api.patch(`/crm/contacts/${contactId}/notes/${note.id}`, { pinned: !note.pinned });
      onChanged?.();
    } catch { toast.error("Couldn't update that note"); }
  };

  const remove = async (note) => {
    try {
      await api.delete(`/crm/contacts/${contactId}/notes/${note.id}`);
      onChanged?.();
    } catch { toast.error("Couldn't delete that note"); }
  };

  const ordered = [...notes].sort((a, b) => (Number(b.pinned) - Number(a.pinned))
    || String(b.created_at).localeCompare(String(a.created_at)));

  return (
    <section data-testid="contact-notes">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-[640]">Notes</h2>
        <span className="text-xs text-muted-foreground" data-testid="contact-notes-privacy">
          Private to you
        </span>
      </div>

      <div className="mt-3 space-y-2">
        <Textarea rows={3} maxLength={10000} value={body} onChange={(e) => setBody(e.target.value)}
          data-testid="contact-note-input" aria-label="Add a note"
          placeholder="What should you remember about this person?" />
        <div className="flex items-center justify-between">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground/80">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
              data-testid="contact-note-remember" className="h-4 w-4 rounded border-input" />
            Remember this
          </label>
          <Button size="sm" onClick={add} disabled={!body.trim() || busy}
            data-testid="contact-note-save">Add note</Button>
        </div>
      </div>

      <ul className="mt-4 space-y-2">
        {ordered.length === 0 && (
          <li className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground"
            data-testid="contact-notes-empty">No notes yet.</li>
        )}
        {ordered.map((n) => (
          <li key={n.id} data-testid={`contact-note-${n.id}`}
            className={`rounded-lg border p-3 text-sm ${n.pinned
              ? "border-amber-300 bg-amber-50/70" : "border-border bg-card"}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="whitespace-pre-wrap text-foreground/90">{n.body}</p>
              <div className="flex shrink-0 gap-1">
                <button type="button" onClick={() => togglePin(n)} aria-pressed={n.pinned}
                  aria-label={n.pinned ? "Unpin note" : "Mark as remember this"}
                  data-testid={`contact-note-pin-${n.id}`}
                  className={`rounded p-1.5 transition-colors hover:bg-secondary ${
                    n.pinned ? "text-amber-700" : "text-muted-foreground"}`}>
                  <Pin className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => remove(n)} aria-label="Delete note"
                  data-testid={`contact-note-delete-${n.id}`}
                  className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {n.pinned && (
              <span className="mt-2 inline-block rounded-full bg-amber-200/70 px-2 py-0.5 text-[11px] font-[600] text-amber-900">
                Remember this
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
